import type { CharacterListItem } from '~/lib/server/actions/character-actions';

/**
 * Category chips shared by the catalog and the home / landing grids, and the
 * rules that decide which characters each chip shows.
 */
export interface Category {
  key: string;
  label: string;
}

export const CATEGORIES: Category[] = [
  { key: 'all', label: 'All' },
  { key: 'popular', label: 'Popular' },
  { key: 'new', label: 'New' },
  // Signed-in only (hidden from the pill row otherwise); matches the hearts
  // the user has saved, not a tag.
  { key: 'favorites', label: 'Favourites' },
  { key: 'milf', label: 'MILF' },
  { key: 'cougar', label: 'Cougar' },
  { key: 'hotwife', label: 'Hotwife' },
  { key: 'voluptuous', label: 'Voluptuous' },
  { key: 'busty', label: 'Busty' },
  { key: 'curvy', label: 'Curvy' },
  { key: 'thick', label: 'Thick' },
  { key: 'stacked', label: 'Stacked' },
  { key: 'succubus', label: 'Succubus' },
  { key: 'nymph', label: 'Nymph' },
  { key: 'vixen', label: 'Vixen' },
  { key: 'seductress', label: 'Seductress' },
  { key: 'temptress', label: 'Temptress' },
  { key: 'bombshell', label: 'Bombshell' },
  { key: 'goddess', label: 'Goddess' },
];

// Category keys that map to built-in filters (sort/style/status). Every other
// key in CATEGORIES is a tag keyword and filters against the character's tags.
export const NON_TAG_CATEGORIES = new Set([
  'all',
  'popular',
  'new',
  'trending',
  'realistic',
  'photorealistic',
  'anime',
  'furry',
]);

// The category pills are marketing keywords that almost never exist verbatim
// in `tags` (real tags are things like "japanese", "athletic", "confident").
// Matching tags alone therefore returned zero characters for every pill. Each
// pill instead matches the real attribute columns (body_type, chest_size,
// age); tag/description keywords still count so admins can hand-tag a
// character into any category.
const bodyIncludes = (c: CharacterListItem, ...parts: string[]) => {
  const bodyType = (c.bodyType ?? '').toLowerCase();
  return parts.some((p) => bodyType.includes(p));
};
const chestIn = (c: CharacterListItem, ...sizes: string[]) =>
  sizes.includes((c.chestSize ?? '').toLowerCase());
const hasKeyword = (c: CharacterListItem, keywords: string[]) => {
  const haystack = [...(c.tags ?? []), c.description ?? '', c.name]
    .join(' ')
    .toLowerCase();
  return keywords.some((k) => haystack.includes(k));
};

export const CATEGORY_MATCHERS: Record<
  string,
  (c: CharacterListItem) => boolean
> = {
  milf: (c) => c.gender === 'female' && (c.age ?? 0) >= 30,
  cougar: (c) => c.gender === 'female' && (c.age ?? 0) >= 38,
  hotwife: (c) =>
    c.gender === 'female' && (c.age ?? 0) >= 27 && (c.age ?? 0) <= 40,
  voluptuous: (c) => bodyIncludes(c, 'voluptuous', 'full-figured'),
  busty: (c) => chestIn(c, 'large', 'huge') || bodyIncludes(c, 'voluptuous'),
  curvy: (c) => bodyIncludes(c, 'curvy', 'voluptuous'),
  thick: (c) => bodyIncludes(c, 'curvy', 'voluptuous') || chestIn(c, 'huge'),
  stacked: (c) =>
    chestIn(c, 'huge') ||
    (chestIn(c, 'large') && bodyIncludes(c, 'curvy', 'voluptuous')),
  succubus: (c) =>
    hasKeyword(c, ['succubus', 'demon', 'devil', 'fantasy', 'intense']),
  nymph: (c) =>
    hasKeyword(c, ['nymph', 'playful', 'flirty', 'spiritual', 'free-spirit']),
  vixen: (c) => hasKeyword(c, ['vixen', 'seductive', 'confident', 'fox']),
  seductress: (c) =>
    hasKeyword(c, ['seductress', 'seductive', 'passionate', 'sensual']),
  temptress: (c) =>
    hasKeyword(c, ['temptress', 'seductive', 'intense', 'sensual']),
  bombshell: (c) =>
    hasKeyword(c, ['bombshell', 'glamorous', 'stunning', 'elegant']) ||
    (chestIn(c, 'large', 'huge') && bodyIncludes(c, 'curvy', 'voluptuous')),
  goddess: (c) =>
    hasKeyword(c, ['goddess', 'divine', 'elegant', 'powerful', 'spiritual']),
};

// Search-box words that mean a category rather than a literal tag. "mature"
// is the one users actually type (support ticket, Sep 2026): only 11
// characters carry it as a tag and four of those are 23-year-old anime boys,
// so the tag alone returned nonsense. Route it through the age-based matcher.
const SEARCH_ALIASES: Record<string, string> = {
  mature: 'milf',
  older: 'milf',
  mom: 'milf',
  mommy: 'milf',
  stepmom: 'milf',
  wife: 'hotwife',
  boobs: 'busty',
  tits: 'busty',
};

/**
 * The search box used to match the character NAME only, so every trait word
 * ("mature", "anime", "dominant", "japanese") produced an empty grid with no
 * hint why. Each whitespace-separated term now has to match at least one of:
 * name, a tag, the style / gender / ethnicity / body-type columns, or a
 * category matcher (directly or via SEARCH_ALIASES). All terms must match.
 */
export function matchesSearch(c: CharacterListItem, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const name = c.name.toLowerCase();
  const tags = (c.tags ?? []).map((t) => t.toLowerCase());
  const attrs = [c.style, c.gender, c.ethnicity, c.bodyType]
    .filter((v): v is string => !!v)
    .map((v) => v.toLowerCase());

  return terms.every((term) => {
    // An aliased word means the category, full stop. Falling through to
    // the tag check let "mature" match two older men tagged "mature".
    const alias = SEARCH_ALIASES[term];
    if (alias) return CATEGORY_MATCHERS[alias]?.(c) ?? false;
    if (name.includes(term)) return true;
    if (tags.some((t) => t === term || t.startsWith(term))) return true;
    if (attrs.some((a) => a === term || a.startsWith(term))) return true;
    return CATEGORY_MATCHERS[term]?.(c) ?? false;
  });
}

/**
 * Whether a character belongs under a chip. Built-in chips (all, popular,
 * new…) never exclude anyone here — they work through status and sort —
 * favourites match the viewer's hearts, keyword chips match a literal tag or
 * the category's attribute rule.
 */
/**
 * Pills defined by real attributes (gender + age). Their rule is the whole
 * definition: the character factory writes "milf" into the tags of trans
 * characters too, and honouring the literal tag put trans characters under
 * MILF. Keyword pills still accept hand-set tags.
 */
const RULE_ONLY_CATEGORIES = new Set(['milf', 'cougar', 'hotwife']);

export function matchesCategory(
  c: CharacterListItem,
  key: string,
  favoriteIds: Set<string>,
): boolean {
  if (key === 'favorites') return favoriteIds.has(c.id);
  if (NON_TAG_CATEGORIES.has(key)) return true;
  return (
    (!RULE_ONLY_CATEGORIES.has(key) &&
      (c.tags ?? []).some((t) => t.toLowerCase() === key.toLowerCase())) ||
    (CATEGORY_MATCHERS[key]?.(c) ?? false)
  );
}
